/*

? The chat's Google Maps pieces (19 Oct 2026, the chat cards redesign): one loader for the whole chat
? (the location dialog and every location card in the thread), and a small map preview with a pin.

  One loader on purpose: @react-google-maps/api loads the script once per page, and a second call with
  a different key or library list is refused. The dialog's search needs "places", so every chat map
  asks for exactly that, with the same key the dialog always used.

  The preview is a picture of a place, not a map to explore: no controls, no dragging or zooming, no
  clickable points of interest. In the thread it is built only once its card scrolls into view, so a
  long conversation never starts a map per location message at once.

*/

/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Component Imports */

import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { useInView } from "react-intersection-observer";

/* Icon Imports */

import { LocationOnOutlined } from "@mui/icons-material";

/* The libraries every chat map loads (the dialog's Places search needs "places"). */
const CHAT_MAP_LIBRARIES: Array<"places"> = ["places"];

/** The chat's one Maps script loader; `isLoaded` turns true once Google Maps is ready. */
export const useChatMapsLoader = (): { isLoaded: boolean } => {
	const { isLoaded } = useLoadScript({
		googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY_NO_REFERRER_RESTRICTIONS || "",
		libraries: CHAT_MAP_LIBRARIES as any,
	});
	return { isLoaded };
};

/* A preview takes no gestures and shows no controls: tapping it is the card's own action. */
const PREVIEW_MAP_OPTIONS = {
	disableDefaultUI: true,
	clickableIcons: false,
	keyboardShortcuts: false,
	gestureHandling: "none",
	draggable: false,
	zoomControl: false,
};

/* Styled Components */

const PreviewFrame = styled("div")(({ theme }) => ({
	position: "relative",
	width: "100%",
	overflow: "hidden",
	background: theme.palette.background.highlight,
	pointerEvents: "none",
}));

/* The pin, drawn over the map's centre with its point on the place. */
const Pin = styled(LocationOnOutlined)(({ theme }) => ({
	position: "absolute",
	left: "50%",
	top: "50%",
	transform: "translate(-50%, -100%)",
	fontSize: "2.25rem",
	color: theme.palette.primary.dark,
	filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25))",
	zIndex: 1,
}));

/**
 * A still map centred on [latitude], [longitude] with a pin, [height] tall. Before the script is
 * ready (or with no coordinates) it is the warm placeholder with the pin, never an empty box.
 * [lazy] builds the map only once the preview scrolls into view (the thread's cards).
 */
export const ChatMapPreview = ({
	latitude,
	longitude,
	height,
	zoom = 15,
	lazy = false,
}: {
	latitude: number | null;
	longitude: number | null;
	height: string;
	zoom?: number;
	lazy?: boolean;
}) => {
	const { isLoaded } = useChatMapsLoader();
	const { ref, inView } = useInView({ triggerOnce: true, rootMargin: "200px 0px", skip: !lazy });
	const hasPlace: boolean = Number.isFinite(latitude) && Number.isFinite(longitude) && latitude !== null && longitude !== null;
	const center = React.useMemo(
		() => ({ lat: Number(latitude || 0), lng: Number(longitude || 0) }),
		[latitude, longitude],
	);
	return (
		<PreviewFrame
			ref={ref}
			sx={{ height }}
		>
			{isLoaded && hasPlace && (!lazy || inView) ? (
				<GoogleMap
					zoom={zoom}
					center={center}
					options={PREVIEW_MAP_OPTIONS as any}
					mapContainerStyle={{ width: "100%", height: "100%" }}
				/>
			) : null}
			<Pin />
		</PreviewFrame>
	);
};
